import * as React from 'react';
import { useUser } from '@clerk/tanstack-react-start';
import { useNavigate } from '@tanstack/react-router';
import { useMutation } from 'convex/react';
import { api } from '@/convex';
import { ProjectSwitcher } from '@/components/ProjectSwitcher';
import { useOrgContext } from '@/hooks/useOrgContext';
import {
  ACTIVE_PROJECT_STORAGE_KEY,
  useProjectContext,
} from '@/hooks/useProjectContext';
import { buildSettingsPath } from '@/lib/routes';
import { getQuota, type PlanId } from '../../../convex/billing/plans';
import s from './DashboardLayout.module.scss';

function slugifyProjectName(name: string) {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return slug || 'project';
}

function createUniqueProjectSlug(
  name: string,
  projects: Array<{ slug: string }>,
) {
  const base = slugifyProjectName(name);
  const existing = new Set(projects.map((project) => project.slug));
  let slug = base;
  let suffix = 2;

  while (existing.has(slug)) {
    slug = `${base}-${suffix}`;
    suffix += 1;
  }

  return slug;
}

export function SidebarProjectHeader() {
  const { org, orgId, isLoading: orgLoading } = useOrgContext();
  const {
    projects,
    resolvedSlug,
    isAllProjects,
    setProject,
    isLoading: projectLoading,
  } = useProjectContext();
  const { user } = useUser();
  const createProject = useMutation(api.projects.create);
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [isCreating, setIsCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const orgName = org?.name ?? 'My Org';
  const orgPlan = (org?.plan ?? 'free') as PlanId;
  const maxProjects = getQuota(orgPlan, 'maxProjects');
  const hasProjectCapacity = projects.length < maxProjects;

  const openCreateProject = React.useCallback(() => {
    if (!hasProjectCapacity) {
      if (org?.slug) {
        navigate({
          to: buildSettingsPath(org.slug) as any,
          search: { tab: 'billing' } as any,
        });
      }
      return;
    }

    setCreateError(null);
    setShowCreate(true);
  }, [hasProjectCapacity, navigate, org?.slug]);

  React.useEffect(() => {
    if (showCreate) inputRef.current?.focus();
  }, [showCreate]);

  React.useEffect(() => {
    function onCreateProject() {
      openCreateProject();
    }
    window.addEventListener('fragments:create-project', onCreateProject);
    return () =>
      window.removeEventListener('fragments:create-project', onCreateProject);
  }, [openCreateProject]);

  if (orgLoading || projectLoading) {
    return <div className={s.switcherSkeleton}>...</div>;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed || !orgId || !user?.id) return;
    const slug = createUniqueProjectSlug(trimmed, projects);
    setIsCreating(true);
    setCreateError(null);
    try {
      await createProject({
        orgId,
        name: trimmed,
        slug,
        clerkUserId: user.id,
      });
      setNewName('');
      setShowCreate(false);
      if (typeof window !== 'undefined') {
        localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, slug);
      }
      navigate({
        to: buildSettingsPath(org!.slug) as any,
        search: { project: slug } as any,
      } as any);
    } catch (error) {
      const message =
        error instanceof Error && error.message.includes('PLAN_LIMIT_EXCEEDED')
          ? 'This workspace has reached its repository limit. Upgrade to add another repository.'
          : 'Repository could not be created. Try a different name or check your workspace permissions.';
      setCreateError(message);
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <>
      <ProjectSwitcher
        projects={projects}
        selectedSlug={resolvedSlug}
        isAllProjects={isAllProjects}
        orgName={orgName}
        onSelect={setProject}
        onCreateProject={openCreateProject}
        createProjectLabel={
          hasProjectCapacity
            ? 'New repository'
            : 'Upgrade for more repositories'
        }
        createProjectDescription={
          hasProjectCapacity
            ? 'Connect a repository next'
            : `${orgPlan === 'free' ? 'Free' : orgPlan} plan includes ${maxProjects} repositor${maxProjects === 1 ? 'y' : 'ies'}`
        }
      />
      {showCreate && (
        <div
          className={s.createProjectOverlay}
          onClick={() => setShowCreate(false)}
        >
          <form
            className={s.createProjectDialog}
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreate}
          >
            <label className={s.createProjectLabel}>New repository</label>
            <p className={s.createProjectHint}>
              Create the repository scope, then connect it from Repository
              settings.
            </p>
            <input
              ref={inputRef}
              className={s.createProjectInput}
              type="text"
              placeholder="Repository name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={isCreating}
              autoComplete="off"
            />
            {createError && (
              <p className={s.createProjectError}>{createError}</p>
            )}
            <div className={s.createProjectActions}>
              <button
                type="button"
                className={s.createProjectCancel}
                onClick={() => setShowCreate(false)}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={s.createProjectSubmit}
                disabled={!newName.trim() || isCreating}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
